import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './Filters.css';


/**
 * Custom hook for handling form state.
 */
function useFilters(callback, defaultFilters) {
  const [filters, setFilters] = useState(defaultFilters);

  const transforms = {
    escalated: e => e.target.checked,
  };

  const handleFilterChange = (event) => {
    if (event.target.name === 'show-all') {
      event.preventDefault();
      callback({}, event.target.name);
      setFilters({ state: '', model: '' });
      return {};
    }

    let newFilters = {};

    if (event.target.name === 'narrative') {
      // A narrative button has been selected.
      // Clear any of the non-narrative filters.
      newFilters = {
        [event.target.name]: event.target.value,
      };
    } else {
      // One of the other filters has changed.
      // Merge in the new value.
      newFilters = {
        ...filters,
        [event.target.name]: (
          transforms[event.target.name]
            ? transforms[event.target.name](event)
            : event.target.value
        ),
      };

      // Make sure the narrative filter is unset.
      delete newFilters.narrative;
    }

    callback(newFilters, event.target.name);
    setFilters(newFilters);
    return newFilters;
  };

  return {
    filters,
    handleFilterChange,
  };
}


function getOption(o) {
  if (Object.prototype.hasOwnProperty.call(o, 'value')) {
    return <option key={o.value} value={o.value}>{o.label}</option>;
  }

  return <option key={o} value={o}>{o}</option>;
}


function filtersUnset(filters) {
  if (filters.narrative) {
    return false;
  }

  if (filters.state) {
    return false;
  }

  if (filters.escalated) {
    return false;
  }

  if (filters.model) {
    return false;
  }

  return true;
}


function Filters({ options, defaultFilters, onChangeFilters }) {
  const { filters, handleFilterChange } = useFilters(onChangeFilters, defaultFilters);

  const filterContainer = React.createRef();

  // HACK: Figure out why this component sometimes renders, even if the
  // options are empty.
  if (!options.state) {
    return false;
  }

  // Render the narrative selection radio buttons.
  // HACK: Hard code the narratives that will be highlighted.
  // TODO: Perhaps pass the name/id pairs in as props.
  const narrativeIds = [1017, 1110, 1357];
  const idToName = {
    1017: 'Cornelius Brown',
    1110: 'Michael Noel',
    1357: 'Peter Gaines',
  };
  const narrativeButtons = narrativeIds.map(id => (
    <React.Fragment key={id}>
      <input
        type="radio"
        id={`narrative-${id}`}
        name="narrative"
        value={id}
        checked={filters.narrative === `${id}`}
        onChange={handleFilterChange}
      />
      <label htmlFor={`narrative-${id}`}>{idToName[id]}</label>
    </React.Fragment>
  ));

  const classNames = ['Filters'];

  return (
    <form className={classNames.join(' ')} ref={filterContainer}>
      <div className="Filters_Inner">
        <div className="Filters_Row">
          {narrativeButtons}
        </div>

        <div className="Filters_Row">
          <input
            type="checkbox"
            id="toggle-escalated"
            name="escalated"
            checked={filters.escalated === true}
            onChange={handleFilterChange}
          />
          <label htmlFor="toggle-escalated">Escalated after Taser</label>

          <select className={filters.state === '' || filters.state === undefined ? 'Filters_Select_State' : 'Filters_Select_State Filters_Select_State_filtered'} id="select-state" name="state" value={filters.state} onChange={handleFilterChange}>
            <option value="">Filter state</option>
            {options.state.map(getOption)}
          </select>

          <select className={filters.model === '' || filters.model === undefined ? 'Filters_Select_Model' : 'Filters_Select_Model Filters_Select_Model_filtered'} id="select-model" name="model" value={filters.model} onChange={handleFilterChange}>
            <option value="">Filter model&nbsp;&nbsp;</option>
            {options.model.map(getOption)}
          </select>

          <input
            type="reset"
            id="show-all"
            name="show-all"
            value="Show All"
            disabled={filtersUnset(filters)}
            onClick={handleFilterChange}
          />
          <label htmlFor="show-all">Show All</label>
        </div>
      </div>
    </form>
  );
}


Filters.defaultProps = {
  options: {},
  defaultFilters: {},
};

Filters.propTypes = {
  options: PropTypes.shape({
    state: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    year: PropTypes.arrayOf(PropTypes.number),
    TASER_ESCALATE: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.bool,
        ]),
      }),
    ),
  }),
  onChangeFilters: PropTypes.func.isRequired,
  defaultFilters: PropTypes.shape({
    narrative: PropTypes.string,
  }),
};


export default Filters;
